// Episodes page - responsive grid using the homepage episode card system
import React, { useState, useEffect } from 'react';
import EpisodeCard from '../components/Episodes/EpisodeCard';
import { fetchEpisodes } from '../api';
import './Episodes.css';

const Episodes = () => {
  const [episodes, setEpisodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const episodesPerPage = 12;

  useEffect(() => {
    const loadEpisodes = async () => {
      try {
        setLoading(true);
        const data = await fetchEpisodes();
        const list = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
        setEpisodes(list);
        setError(null);
      } catch (err) {
        console.error('Failed to fetch episodes:', err);
        setError(err.message);
        setEpisodes([]);
      } finally {
        setLoading(false);
      }
    };

    loadEpisodes();
  }, [loadAttempt]);

  // Pagination logic
  const indexOfLastEpisode = currentPage * episodesPerPage;
  const indexOfFirstEpisode = indexOfLastEpisode - episodesPerPage;
  const currentEpisodes = episodes.slice(indexOfFirstEpisode, indexOfLastEpisode);
  const totalPages = Math.ceil(episodes.length / episodesPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (loading) {
    return (
      <div className="episodes-page">
        <div className="episodes-page-header">
          <h1>All Episodes</h1>
          <p>Loading episodes...</p>
        </div>
        <div className="episodes-grid loading">
          {Array.from({ length: 6 }).map((_, index) => (
            <div className="a-card a-card--skeleton" key={index} aria-hidden="true" />
          ))}
        </div>
      </div>
    );
  }

  if (error && episodes.length === 0) {
    return (
      <div className="episodes-page">
        <div className="episodes-page-header">
          <h1>All Episodes</h1>
          <div className="episodes-page-error-message">
            <p>Failed to load episodes: {error}</p>
            <button type="button" onClick={() => setLoadAttempt((attempt) => attempt + 1)}>Try Again</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="episodes-page">
      <div className="episodes-page-header">
        <h1>All Episodes</h1>
        <p className="episodes-count">
          {episodes.length} episode{episodes.length !== 1 ? 's' : ''} available
        </p>
      </div>

      <div className="episodes-grid">
        {currentEpisodes.map((episode) => (
          <EpisodeCard key={episode.id} episode={episode} />
        ))}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="episodes-page-pagination">
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            className="episodes-page-pagination-btn"
          >
            <i className="fas fa-chevron-left"></i>
            Previous
          </button>

          <div className="episodes-page-pagination-numbers">
            {Array.from({ length: totalPages }, (_, index) => {
              const pageNumber = index + 1;
              const isCurrentPage = pageNumber === currentPage;
              const showPage = 
                pageNumber === 1 ||
                pageNumber === totalPages ||
                (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1);

              if (!showPage) {
                if (pageNumber === currentPage - 2 || pageNumber === currentPage + 2) {
                  return <span key={pageNumber} className="episodes-page-pagination-ellipsis">...</span>;
                }
                return null;
              }

              return (
                <button
                  key={pageNumber}
                  onClick={() => handlePageChange(pageNumber)}
                  className={`episodes-page-pagination-number ${isCurrentPage ? 'active' : ''}`}
                >
                  {pageNumber}
                </button>
              );
            })}
          </div>

          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === totalPages}
            className="episodes-page-pagination-btn"
          >
            Next
            <i className="fas fa-chevron-right"></i>
          </button>
        </div>
      )}

      {/* Episodes Stats */}
      <div className="episodes-page-stats">
        <div className="episodes-page-stat-item">
          <span className="episodes-page-stat-number">{episodes.length}</span>
          <span className="episodes-page-stat-label">Total Episodes</span>
        </div>
        <div className="episodes-page-stat-item">
          <span className="episodes-page-stat-number">
            {Math.round(episodes.reduce((total, episode) => {
              const parts = String(episode.itunes_duration || '0').split(':').map(Number);
              if (parts.some(Number.isNaN)) return total;
              const seconds = parts.length === 3
                ? (parts[0] * 3600) + (parts[1] * 60) + parts[2]
                : (parts[0] * 60) + (parts[1] || 0);
              return total + seconds;
            }, 0) / 60)}m
          </span>
          <span className="episodes-page-stat-label">Total Duration</span>
        </div>
        <div className="episodes-page-stat-item">
          <span className="episodes-page-stat-number">
            {episodes.length > 0 ? Math.max(...episodes.map(ep => parseInt(ep.season_number, 10) || 1)) : 0}
          </span>
          <span className="episodes-page-stat-label">Seasons</span>
        </div>
      </div>
    </div>
  );
};

export default Episodes;
